<template>
    <div>
        <div class="relative flex flex-row justify-center">
            <div aria-hidden="true" class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-gray-200" :class="props.borderClass" />
            </div>
            <Tooltip class="relative" :is-teleported="true">
                <template #trigger="tooltipProps">
                    <button
                        type="button"
                        class="bg-gray-50 inline-flex flex-row items-center gap-1 rounded-full pl-3 pr-2 py-1.5 font-semibold text-gray-600 ring-1 ring-inset ring-gray-200 hover:bg-gray-700 hover:text-gray-50 cursor-pointer active:bg-gray-900 active:text-gray-50 group focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gray-700 text-sm"
                        :aria-expanded="props.isExpanded"
                        v-bind="tooltipProps"
                        @click="
                            triggerHideTooltip();
                            emit('toggle-is-expanded');
                        "
                    >
                        <slot name="label" />
                        <ChevronRightIcon
                            class="size-5 text-gray-400 group-hover:text-gray-300 transition-transform"
                            :class="[props.isExpanded ? '-rotate-90 -translate-y-px' : 'rotate-90 translate-y-px']"
                        />
                    </button>
                </template>

                <template #text>
                    <template v-if="props.isExpanded">
                        <slot name="tooltip-expanded" />
                    </template>

                    <template v-else>
                        <slot name="tooltip-collapsed" />
                    </template>
                </template>
            </Tooltip>
        </div>

        <div
            class="transition-[height] overflow-hidden pt-1"
            :class="[props.isExpanded ? 'h-auto pb-1' : 'h-0']"
            :inert="!props.isExpanded"
        >
            <slot name="content" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { provide, ref } from 'vue';
import ChevronRightIcon from '../Icon/ChevronRightIcon.vue';
import Tooltip from '../Tooltip/Tooltip.vue';
import { triggerHideTooltipKey } from '../Tooltip/injectionKeys';

type ExpandableSectionProps = {
    isExpanded: boolean;
    borderClass?: string;
};

type ExpandableSectionEmits = {
    (e: 'toggle-is-expanded'): void;
};

const props = defineProps<ExpandableSectionProps>();
const emit = defineEmits<ExpandableSectionEmits>();

// This is used to force close the Tooltip after clicking the button
const triggerHideTooltipKeyCounter = ref(0);
provide(triggerHideTooltipKey, triggerHideTooltipKeyCounter);

function triggerHideTooltip() {
    triggerHideTooltipKeyCounter.value = triggerHideTooltipKeyCounter.value + 1;
}
</script>
